// Wallet requisites templates on this side (x-backend V26, cabinet-db
// WalletRequisites): which template a wallet's gateway names, and the same checks
// the cabinet makes, so an operator sees what is wrong before Save rather than in
// a 422. The server stays the authority — it normalizes and refuses — and serves
// the templates themselves (GET /api/public-wallets/requisites-templates), so the
// fields are never listed twice.
//
// Pure functions with tests, like jsonObjectValue: src/i18n.js uses top-level
// await, so the editor component cannot be imported under jest. Problems are
// i18n keys with their parameters, translated by whoever shows them.

export const MAX_TEXT = 200;

const CARD_SEPARATORS = /[\s -]/g;
const PHONE_SEPARATORS = /[\s ().-]/g;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * The template the wallet's gateway names, from the gateways' light list and the
 * served templates; null when there is none, or either is not loaded — the editor
 * then falls back to free-form, which the server still checks.
 */
export const templateFor = (gatewayId, gateways, templates) => {
    if (gatewayId === null || gatewayId === undefined || gatewayId === '') return null;
    const gateway = (gateways || []).find((g) => g && String(g.id) === String(gatewayId));
    const name = gateway?.walletRequisitesTemplate;
    if (!name) return null;
    return (templates || []).find((t) => t.template === name) || null;
};

/** A card number as the digits alone, whatever separated them. */
export const cardDigits = (text) => String(text ?? '').replace(CARD_SEPARATORS, '');

/** 4111111111111111 as 4111 1111 1111 1111, for reading. */
export const groupCard = (digits) => cardDigits(digits).replace(/(\d{4})(?=\d)/g, '$1 ');

export const luhn = (digits) => {
    let sum = 0;
    let twice = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let digit = digits.charCodeAt(i) - 48;
        if (twice) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        twice = !twice;
    }
    return sum % 10 === 0;
};

const phoneForm = (text) => {
    const phone = text.replace(PHONE_SEPARATORS, '');
    return phone.startsWith('00') ? `+${phone.slice(2)}` : phone;
};

/**
 * What is wrong with one field's text, as `{ key, params }`, or null. Mirrors
 * WalletRequisites.normalize: an empty value is absent, and only a required field
 * minds that.
 */
export const fieldProblem = (field, raw) => {
    if (raw !== undefined && raw !== null && typeof raw !== 'string') {
        return { key: 'walletRequisites.errors.notText' };
    }
    const value = (raw || '').trim();
    if (value === '') {
        return field.required ? { key: 'walletRequisites.errors.required' } : null;
    }
    switch (field.format) {
        case 'card_number': {
            const digits = cardDigits(value);
            if (!/^\d{13,19}$/.test(digits)) return { key: 'walletRequisites.errors.cardLength' };
            return luhn(digits) ? null : { key: 'walletRequisites.errors.cardCheckDigit' };
        }
        case 'phone':
            return /^\+?\d{8,15}$/.test(phoneForm(value)) ? null : { key: 'walletRequisites.errors.phone' };
        case 'crypto_address':
            if (!/^[A-Za-z0-9:_-]+$/.test(value)) return { key: 'walletRequisites.errors.addressChars' };
            return value.length >= 20 && value.length <= 128
                ? null
                : { key: 'walletRequisites.errors.addressLength' };
        case 'choice': {
            const options = field.options || [];
            return options.some((o) => o.toLowerCase() === value.toLowerCase())
                ? null
                : { key: 'walletRequisites.errors.choice', params: { options: options.join(', ') } };
        }
        default:
            if (CONTROL.test(value)) return { key: 'walletRequisites.errors.oneLine' };
            return value.length > MAX_TEXT ? { key: 'walletRequisites.errors.tooLong', params: { max: MAX_TEXT } } : null;
    }
};

/** The keys of `value` that are not fields of the template. */
export const extraKeys = (template, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const known = new Set(template.fields.map((f) => f.key));
    return Object.keys(value).filter((key) => !known.has(key));
};

/**
 * Every problem with `value` under the template: `{ fields: { key: problem },
 * extra: [keys] }`. `ok` when there are none.
 */
export const requisitesProblems = (template, value) => {
    const fields = {};
    const object = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    template.fields.forEach((field) => {
        const problem = fieldProblem(field, object[field.key]);
        if (problem) fields[field.key] = problem;
    });
    const extra = extraKeys(template, object);
    return { fields, extra, ok: Object.keys(fields).length === 0 && extra.length === 0 };
};

/**
 * The value the template's form emits: its fields in its order, blanks left out,
 * then whatever else was there (`keepExtra`), so editing one field never drops a
 * key on the operator's behalf. Null when nothing is left, so `required` applies.
 */
export const templateValue = (template, value, keepExtra = true) => {
    const object = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const out = {};
    template.fields.forEach(({ key }) => {
        const text = object[key];
        if (typeof text === 'string' && text.trim() !== '') out[key] = text;
    });
    if (keepExtra) {
        extraKeys(template, object).forEach((key) => {
            out[key] = object[key];
        });
    }
    return Object.keys(out).length ? out : null;
};

/** The free-form editor's preset chips, from the templates: their key names. */
export const presetsFromTemplates = (templates) =>
    (templates || []).map((t) => ({ label: t.template, keys: t.fields.map((f) => f.key) }));

/** The CSV header a gateway with this template takes, as WalletCsvService wants it. */
export const csvHeader = (template, separator = ';') =>
    [...template.fields.map((f) => f.key), 'is_active'].join(separator);
